// V3 — split each per-level cell into onshore / nearshore / offshore FTE.
//
// Scenario-driven: the split comes from `profile.ogs.disciplines[]` — a single
// combined "OGS" bucket per discipline. The chosen hub's `shore` decides
// whether the OGS FTE lands in the nearshore or offshore leg (so downstream
// cost + rollups stay accurate), but the user only sees one % per discipline.
// A discipline with no config, disabled, or a level outside its `levels`
// selection stays 100% onshore.

import type {
  ClientProfile,
  LevelId,
  Model,
  OgsDisciplineConfig,
} from '../types.js';

export interface ShoreSplit {
  onshore: number;
  nearshore: number;
  offshore: number;
}

/** Resolve the OGS config for a (discipline, channel) cell. Prefers an exact
 *  channel match, then falls back to a discipline-wide entry (`channelId`
 *  omitted or `'all'`) so single-channel disciplines and legacy configs still
 *  resolve. Returns undefined when nothing applies. */
export function resolveOgsDisciplineConfig(
  profile: ClientProfile,
  disciplineId: string,
  channelId?: string,
): OgsDisciplineConfig | undefined {
  const list = profile.ogs?.disciplines;
  if (!list) return undefined;
  const forDiscipline = list.filter((d) => d.disciplineId === disciplineId);
  if (channelId !== undefined) {
    const exact = forDiscipline.find((d) => d.channelId === channelId);
    if (exact) return exact;
  }
  return forDiscipline.find((d) => d.channelId == null || d.channelId === 'all');
}

/** The shore ('nearshore' | 'offshore') the chosen hub represents. Defaults to
 *  'offshore' when the hub can't be resolved so OGS FTE is still costed. */
export function ogsShoreForConfig(
  model: Model,
  cfg: OgsDisciplineConfig | undefined,
): 'nearshore' | 'offshore' {
  const hub = (model.ogsHubs || []).find((h) => h.id === cfg?.hubId);
  return hub?.shore ?? 'offshore';
}

/** Whether the OGS bucket applies to this (discipline, level) cell. */
function ogsAppliesToCell(
  cfg: OgsDisciplineConfig | undefined,
  levelId: LevelId,
): boolean {
  if (!cfg || !cfg.enabled) return false;
  if (!cfg.hubId) return false;
  if (!Array.isArray(cfg.levels) || !cfg.levels.includes(levelId)) return false;
  return (Number(cfg.pct) || 0) > 0;
}

export function splitShore(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
  adjustedFte: number,
): ShoreSplit {
  if (adjustedFte <= 0) return { onshore: 0, nearshore: 0, offshore: 0 };

  const cfg = resolveOgsDisciplineConfig(profile, disciplineId, channelId);
  if (!ogsAppliesToCell(cfg, levelId)) {
    return { onshore: adjustedFte, nearshore: 0, offshore: 0 };
  }

  const pct = Math.min(1, Math.max(0, Number(cfg!.pct) || 0));
  const ogsFte = adjustedFte * pct;
  const onshore = adjustedFte - ogsFte;
  const shore = ogsShoreForConfig(model, cfg);

  return {
    onshore,
    nearshore: shore === 'nearshore' ? ogsFte : 0,
    offshore: shore === 'offshore' ? ogsFte : 0,
  };
}
